<script lang="ts" setup>
import { chartColors } from '@forgd/ui-kit/charts/lib/colors'
import { formatValue } from '@forgd/formatter'
import { Card } from '@forgd/ui-kit/card'
import { Skeleton } from '@forgd/ui-kit/skeleton'
import { useQuery } from '@pinia/colada'

const TOP_RETURN_TIMEFRAME = '30d'
const FLOAT_BUCKETS_TIMEFRAME = '1m'

const priceChangeQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/price-change-vs-initial-circ-supply', {
    params: () => ({ query: {} }),
  }),
  refetchOnWindowFocus: false,
})

const fdvQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/fdv-by-initial-circ-supply', {
    params: () => ({ query: {} }),
  }),
  refetchOnWindowFocus: false,
})

const airdropQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/airdrop-value-vs-price-performance', {
    params: () => ({ query: {} }),
  }),
  refetchOnWindowFocus: false,
})

const floatBucketQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/price-performance-by-float-bucket', {
    params: () => ({ query: {} }),
  }),
  refetchOnWindowFocus: false,
})

const isLoading = computed(() =>
  priceChangeQuery.isLoading.value
  || fdvQuery.isLoading.value
  || airdropQuery.isLoading.value
  || floatBucketQuery.isLoading.value,
)

const priceChangeSparkline = computed(() => {
  const order = ['24h', '7d', '30d', '60d', '90d', '180d', '365d'] as const
  const summary = priceChangeQuery.data.value?.summary ?? []
  const byTf = new Map(summary.map(s => [s.timeframe, s.avgPriceChangePercentage]))
  return order
    .map((tf, i) => {
      const y = byTf.get(tf)
      return y !== undefined ? { x: i, y } : null
    })
    .filter((d): d is { x: number, y: number } => d !== null)
})

const positiveReturnsSparkline = computed(() => {
  const series = floatBucketQuery.data.value?.priceChangeTimeSeries ?? []
  if (!series.length) {
    return []
  }
  // Pre-build per-bucket maps so the day×bucket walk is O(buckets × days) not O(buckets × days²).
  const bucketMaps = series.map(s => new Map(s.data.map(d => [d.daysPostTge, d.avgPriceChangePercentage])))
  const days = series[0]!.data.map(d => d.daysPostTge)
  return days.map((day, i) => {
    const values = bucketMaps
      .map(m => m.get(day))
      .filter((v): v is number => typeof v === 'number')
    const mean = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0
    return { x: i, y: mean }
  })
})

// Maps a backend `{ date, value }[]` series to ChartSparkline-ready `[{ x, y }]`,
// skipping null/missing points so the chart never renders a NaN.
function toSparkline(series: { date: string, value: number | null }[] | undefined) {
  return (series ?? [])
    .map((point, i) => (typeof point.value === 'number' ? { x: i, y: point.value } : null))
    .filter((d): d is { x: number, y: number } => d !== null)
}

// The "All" group carries the aggregate FDV figures and sparkline series.
const allFdvGroup = computed(() => fdvQuery.data.value?.groups?.find(g => g.group === 'All'))

const marketCapSparkline = computed(() => toSparkline(allFdvGroup.value?.averageFdvTimeSeries))

const airdropSparkline = computed(() => toSparkline(airdropQuery.data.value?.meanAirdropTimeSeries))

const avg30dReturn = computed(() => {
  const entry = priceChangeQuery.data.value?.summary?.find(s => s.timeframe === TOP_RETURN_TIMEFRAME)
  return entry?.avgPriceChangePercentage ?? null
})

// Average FDV at launch is the closest proxy in the dataset to a TGE market cap.
const avgMarketCapAtTge = computed(() => allFdvGroup.value?.averageFdv ?? null)

const avgAirdropAtTge = computed(() => airdropQuery.data.value?.summary?.meanAirdropUsd ?? null)

const tokensWithPositiveReturns = computed(() => {
  const items = floatBucketQuery.data.value?.tokensWithPositiveReturnsByFloatBucket ?? []
  const filtered = items
    .filter(i => i.timeframe === FLOAT_BUCKETS_TIMEFRAME)
    .filter((i): i is typeof i & { positiveReturnsPercentage: number } => i.positiveReturnsPercentage !== null)
  if (!filtered.length) {
    return null
  }
  return filtered.reduce((acc, i) => acc + i.positiveReturnsPercentage, 0) / filtered.length
})

const returnTrendColor = computed(() => ((avg30dReturn.value ?? 0) >= 0 ? '#16a34a' : '#dc2626'))

const metrics = computed(() => [
  {
    label: 'Avg. 30D Return (%)',
    value: formatValue(avg30dReturn.value, 'percent:1'),
    valueColor: (avg30dReturn.value ?? 0) >= 0 ? 'text-success' : 'text-error',
    trend: priceChangeSparkline.value,
    trendColor: returnTrendColor.value,
  },
  {
    label: 'Avg. Market Capitalization at TGE',
    value: formatValue(avgMarketCapAtTge.value, 'currency:short,2'),
    valueColor: 'text-primal',
    trend: marketCapSparkline.value,
    trendColor: chartColors.tokenDesigner[1]!,
  },
  {
    label: 'Avg. Airdrop at TGE',
    value: formatValue(avgAirdropAtTge.value, 'currency:short,2'),
    valueColor: 'text-primal',
    trend: airdropSparkline.value,
    trendColor: chartColors.tokenDesigner[1]!,
  },
  {
    label: 'Tokens with Positive Returns',
    value: formatValue(tokensWithPositiveReturns.value, 'percent:1'),
    valueColor: 'text-primal',
    trend: positiveReturnsSparkline.value,
    trendColor: chartColors.tokenDesigner[1]!,
  },
])
</script>

<template>
  <Card class="grid grid-cols-1 divide-y divide-default lg:grid-cols-4 lg:divide-x lg:divide-y-0">
    <div v-for="m in metrics" :key="m.label" class="flex items-start gap-2 p-4 lg:flex-col lg:gap-3">
      <div class="flex min-w-0 flex-1 flex-col gap-1 lg:w-full lg:gap-3">
        <span class="text-2xs text-secondary">{{ m.label }}</span>
        <Skeleton v-if="isLoading" class="h-5 w-20 lg:h-7 lg:w-24" />
        <span v-else class="text-sm font-semibold lg:text-lg" :class="m.valueColor">{{ m.value }}</span>
      </div>
      <div class="h-10 w-17.5 shrink-0 lg:w-full">
        <ChartSparkline
          v-if="m.trend.length"
          :data="m.trend"
          x-axis="x"
          y-axis="y"
          :color="m.trendColor"
          :fill-opacity="0.08"
          width="100%"
          height="40"
        />
      </div>
    </div>
  </Card>
</template>
